import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Party } from './Party';
import { Person } from './Person';
import { Entity } from './Entity';
import { Address } from './Address';
import { Phone } from './Phone';
import { Utils } from '../Utils';
import { Policy } from './Policy';

@supertypeClass({ toClient: true, toServer: true })
export class SplitParty extends Everything /**SplitPartyCoreMixin(Everything)*/ {
  @property({ getType: () => Policy })
  policy: Policy;

  //@TODO copied from SplitPartyCore
  @property({ rule: ['required', 'numeric'] })
  ratio: number;

  //@TODO copied from SplitPartyCore
  @property({ isLocal: true })
  ratioChanged: boolean;
  //@TODO copied from SplitPartyCore
  @property()
  partyProperty: string;

  /*
    equal split of 3 members goes as [33.34, 33.33 , 33.33]
    overrides are left as such and remaining elements gets split in the same logic as above.
    overriding one as 25. the final one looks like
    [ 25, 37.5, 37.5 ]
    Check Beneficiary.test.ts for better understanding all possible cases
    */
  setRatios(parties) {
    let variableCount = 0;
    let fixedPercent = 0;

    // See how many slots not changed by user
    for (let ix = 0; ix < parties.length; ++ix) {
      if (parties[ix].ratioChanged) {
        fixedPercent += Number(parties[ix].ratio) * 1;
      } else {
        variableCount++;
      }
    }

    // If we are stuck between a rock and a hard place reset all but current
    if (!variableCount || fixedPercent > 100) {
      for (let ix = 0; ix < parties.length; ++ix) {
        if (parties[ix] !== this || parties.length === 1) {
          parties[ix].ratioChanged = false;
        }
      }
    }

    // See how much of the percent is fixed because user touched it
    fixedPercent = 0;
    variableCount = 0;

    for (let ix = 0; ix < parties.length; ++ix) {
      if (parties[ix].ratioChanged) {
        fixedPercent += Number(parties[ix].ratio) * 1;
      } else {
        variableCount++;
      }
    }

    const splitUpRatio = Utils.splitRemainderPercentile(fixedPercent, variableCount);
    let ratioIndex = 0;
    for (let ix = 0; ix < parties.length; ++ix) {
      if (!parties[ix].ratioChanged) {
        parties[ix].ratio = splitUpRatio[ratioIndex];
        ratioIndex++;
      }
    }
  }

  constructor() {
    super();
  }

  static new(policy, partyProperty) {
    return new Beneficiary().init(policy, partyProperty);
  }

  init(policy, partyProperty) {
    this.policy = policy;
    this.partyProperty = partyProperty;
    return this;
  }

  ratioTrigger() {
    // Avoid running triggers during final validation
    if (this.policy.isValidating) {
      return;
    }

    // Depend on container to maintain reference to collection
    if (this.ratio > 100 || this.ratio < 0) {
      this.ratio = 0;
    } else {
      this.ratioChanged = true;
    }

    if (this.policy[this.partyProperty]) {
      this.setRatios(this.policy[this.partyProperty]);
    }
  }
}

@supertypeClass({ toClient: true, toServer: true })
export class Beneficiary extends SplitParty {
  /* Used by Persistor to classify Beneficiaries as primary or contingent*/
  @property()
  type: string;

  @property({
    rule: 'required',
    values: {
      '0': 'Unknown',
      '1': 'Named Individual',
      '2': 'Estate',
      '3': 'Complex',
      '4': 'Creditors',
      '5': 'Fractions',
      '6': 'Last Will and Testament',
      '7': 'Trust Agreement',
      '8': 'Split Dollar',
      '9': 'Spouse',
      '10': 'Domestic partner',
      '11': 'Children of marriage',
      '12': 'Children born of marriage.',
      '13': 'Children',
      '14': 'Legally adopted children of insured',
      '15': 'Lawful children of insured',
      '16': 'Brothers of Insured',
      '17': 'Sisters of Insured',
      '18': 'Siblings of Insured',
      '19': 'Uniform Gifts to Minors',
      '20': 'Parents of insured',
      '21': 'PVT CSC Primary Beneficiary',
      '22': 'PVT CSC Child of Primary Beneficiary',
      '23': 'PVT CSC Parents of Primary Beneficiary',
      '24': 'Business Agreement',
      '25': 'Spouse and Children',
      '26': 'Estate of Primary Beneficiary',
      '27': 'Children of Primary Beneficiary',
      '28': 'Parents of Primary Beneficiary',
      '30': 'Ex-Spouse of Insured',
      '2147483647': 'Other',
    },
  })
  relationship: string;

  /**
   * HL-15833: Updated values and descriptions for this prop are maintained in App Question Metadata
   */
  @property({
    rule: 'required',
    values: ['9', '13', '1', '2', '6', '7'],
    descriptions: {
      '9': 'Spouse',
      '13': 'Child',
      '1': 'Other Named Individual',
      '2': 'My Estate',
      '6': 'Trust Under Will',
      '7': 'Other Trust',
    },
  })
  beneType: string;

  @property({ isObsolete: true, rule: ['date', 'required'], sensitiveData: true })
  dob: Date;

  @property({ length: 40, rule: ['name', 'required'] })
  fullName: string;

  @property()
  party: Party;

  @property()
  otherParent: Party;

  @property()
  financiallyImpacted: boolean;

  @property()
  isMinor: boolean;

  @property()
  perStirpes: boolean;

  @property()
  irrevocable: boolean;

  constructor() {
    super();
  }

  static new(policy, partyProperty) {
    return new Beneficiary().init(policy, partyProperty);
  }

  init(policy, partyProperty) {
    super.init(policy, partyProperty);
    return this;
  }

  getRelationshipDescription() {
    return this.__values__('relationship')[this.relationship];
  }

  getTypeDescription() {
    return this.__descriptions__('beneType')[this.beneType];
  }

  fullNameDependsOn() {
    return this.type === 'trust' || this.type === 'trustwill';
  }

  get person() {
    return this.party instanceof Person ? this.party : null;
  }

  set person(party) {
    this.party = party;
  }

  personDependsOn() {
    return this.type === 'individual';
  }

  get entity() {
    return this.party instanceof Entity ? this.party : null;
  }

  set entity(party) {
    this.party = party;
  }

  entityDependsOn() {
    return this.type === 'trust';
  }

  get address() {
    return this.party.mailingAddress;
  }

  set address(address) {
    this.party.mailingAddress = address;
  }

  addressDependsOn() {
    return this.type !== 'estate';
  }

  get phone() {
    return this.party.phone;
  }

  set phone(phone) {
    this.party.phone = phone;
  }

  phoneDependsOn() {
    return this.type !== 'estate';
  }

  isPrimary() {
    return this.partyProperty === 'primaryBeneficiary';
  }

  isContingent() {
    return this.partyProperty === 'contingentBeneficiary';
  }

  isTrustOrEntity() {
    return this.party instanceof Entity;
  }

  getFullName() {
    if (this.isTrustOrEntity()) {
      if (this.entity.trustType === 'revocable') {
        return this.entity.getFullName() + ', revocable';
      } else {
        return this.entity.getFullName() + ', irrevocable';
      }
    }
    return this.person.getFullName();
  }

  getGovtId() {
    return this.isTrustOrEntity() ? this.entity.taxId : this.person.SSN;
  }

  getGovtIdType() {
    return this.isTrustOrEntity() ? this.entity.getTaxIdType() : this.person.getTaxIdType();
  }

  getDob() {
    return this.isTrustOrEntity() ? this.entity.formed : this.person.dob;
  }

  getAddress(): Address {
    return this.isTrustOrEntity() ? this.entity.legalAddress : this.address;
  }

  getPhone(): Phone {
    return this.isTrustOrEntity() ? this.entity.phone : this.phone;
  }

  getPerStirpes() {
    if (!this.policy) {
      return;
    }
    return this.isPrimary() ? this.policy.beneficiaryPerStirpes : this.policy.contingentBeneficiaryPerStirpes;
  }

  beneTypeTrigger() {
    // legal entities can't be minors
    // but your spouse can be a minor ¯\_(ツ)_/¯
    if (this.beneType === '2' || this.beneType === '6' || this.beneType === '7') {
      this.isMinor = undefined;
    }

    if (this.beneType !== '1') {
      this.financiallyImpacted = undefined;
    }

    if (this.beneType) {
      this.policy._newBeneficiary(this.partyProperty, this.beneType, this);
    }
  }
}
